import {
  ArrowRightOutlined,
  CheckCircleFilled,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { App, Button, Form, Input, Typography } from '@wanjianshi/admin-ui';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/auth/store';
import { AdminThemeSwitch } from '@/components/AdminThemeSwitch';
import { WanjianshiLogo } from '@/components/WanjianshiLogo';

export function LoginPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const status = useAdminAuth(state => state.status);
  const login = useAdminAuth(state => state.login);
  const [submitting, setSubmitting] = useState(false);
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return (
    <main className="login-page">
      <AdminThemeSwitch className="login-theme-switch" />
      <section className="login-shell">
        <aside className="login-visual">
          <div className="login-visual__grid" aria-hidden="true" />
          <div className="login-visual__topbar">
            <WanjianshiLogo className="login-visual__logo" />
            <span className="login-visual__edition">管理后台</span>
          </div>

          <div className="login-visual__content">
            <span className="login-visual__eyebrow">WANJIANSHI ADMIN</span>
            <Typography.Title level={1}>
              让平台管理
              <br />
              <span>清晰、高效、可追溯</span>
            </Typography.Title>
            <Typography.Paragraph>
              面向万剪师运营和管理人员的统一工作台，集中处理用户、内容、生成任务及平台资源。
            </Typography.Paragraph>

            <div className="login-visual__features">
              <div className="login-visual__feature">
                <CheckCircleFilled />
                <span><strong>独立身份体系</strong><small>后台与用户端会话完全隔离</small></span>
              </div>
              <div className="login-visual__feature">
                <CheckCircleFilled />
                <span><strong>精细权限控制</strong><small>角色、菜单与操作权限分级管理</small></span>
              </div>
              <div className="login-visual__feature">
                <CheckCircleFilled />
                <span><strong>关键操作留痕</strong><small>重要管理行为可审计、可追踪</small></span>
              </div>
            </div>
          </div>

          <div className="login-visual__footer">万剪师 · 让创意更简单</div>
        </aside>

        <div className="login-form-panel">
          <div className="login-form-panel__inner">
            <div className="login-form-panel__header">
              <span className="login-form-panel__eyebrow">管理员登录</span>
              <Typography.Title level={2}>欢迎</Typography.Title>
              <Typography.Paragraph>请输入已获得后台角色授权的账户信息</Typography.Paragraph>
            </div>

            <Form
              className="login-form"
              layout="vertical"
              size="large"
              requiredMark={false}
              onFinish={async values => {
                setSubmitting(true);
                try {
                  await login(values.account, values.password);
                  message.success('登录成功');
                  const from = (location.state as { from?: string } | null)?.from ?? '/';
                  navigate(from, { replace: true });
                } catch (error) {
                  message.error(error instanceof Error ? error.message : '登录失败');
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              <Form.Item
                name="account"
                label="管理员账户"
                rules={[{ required: true, message: '请输入管理员账户' }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="用户名 / 手机号 / 邮箱"
                  autoComplete="username"
                  autoFocus
                />
              </Form.Item>
              <Form.Item
                name="password"
                label="登录密码"
                rules={[{ required: true, message: '请输入登录密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="请输入登录密码"
                  autoComplete="current-password"
                />
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={submitting}
                className="login-submit-button"
              >
                <span>进入管理后台</span>
                {!submitting ? <ArrowRightOutlined /> : null}
              </Button>
            </Form>

            <div className="login-form-panel__footer">
              <SafetyCertificateOutlined />
              仅限已授权的万剪师后台管理员使用
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
